/**
 * Dashboard.js — Overview tab
 *
 * Replaces the original 14-card + 3-chart layout with:
 *   - 4 hero cards:  Active / Idle / Dormant / Waitlist Pending
 *   - 1 primary chart: stacked bar (focus-only vs focus+thought entries per day)
 *   - 2 supporting panels: platform totals (left) and user growth sparkline (right)
 *
 * Data sources (all existing endpoints):
 *   getActivitySummary()   — new Phase 2.6 endpoint for hero cards
 *   getKnowledgeMetrics()  — daily entries/thoughts for stacked bar
 *   getUserMetrics()       — daily new-user registrations for sparkline
 *   getDocumentMetrics()   — total document count for platform totals
 *   getProviderHealth()    — AI provider health line (October 28, 2026);
 *                            fetched on its own so a failure there never
 *                            blanks the rest of the tab
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer
} from 'recharts';
import { apiService } from '../../services/api';

// =========================================
// Color Palette (Phase 0.4)
// =========================================

const COLORS = {
  blue:   '#3B8BD4',
  green:  '#1D9E75',
  amber:  '#EF9F27',
  coral:  '#D85A30',
  purple: '#7F77DD',
};

// =========================================
// Date Formatter (Phase 0.3)
// =========================================

const formatDateTick = (value) => {
  const date = new Date(value + 'T00:00:00'); // avoid timezone shift
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// =========================================
// Timeframe Filters
// =========================================

const TIMEFRAMES = [
  { key: '7d',   label: '1W' },
  { key: '30d',  label: '1M' },
  { key: '90d',  label: '3M' },
  { key: '365d', label: '1Y' },
];

// =========================================
// Custom Tooltip
// =========================================

const StackedBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const focusOnly = payload.find(p => p.dataKey === 'focusOnly');
  const withThought = payload.find(p => p.dataKey === 'withThought');
  const total = (focusOnly?.value || 0) + (withThought?.value || 0);

  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip-label">{formatDateTick(label)}</p>
      <p style={{ color: COLORS.blue }}>Focus only: {focusOnly?.value || 0}</p>
      <p style={{ color: COLORS.green }}>With thought: {withThought?.value || 0}</p>
      <p className="chart-tooltip-total">Total: {total}</p>
    </div>
  );
};

// =========================================
// Provider health line (October 28, 2026)
// =========================================

const PROVIDER_LABELS = { openai: 'OpenAI', anthropic: 'Anthropic' };

/**
 * "OpenAI: OK" or "OpenAI: quota failure at 16:02, 3 failures in 15 min".
 * A provider counts as failing while it has failures inside the window; an
 * older failure is history and reads OK.
 */
function providerHealthText(key, record, windowMinutes) {
  const label = PROVIDER_LABELS[key] || key;
  const count = record?.failuresLast15Min || 0;
  if (!record?.lastFailure || count === 0) return { label, text: `${label}: OK`, failing: false };
  const at = new Date(record.lastFailure.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const noun = count === 1 ? 'failure' : 'failures';
  return {
    label,
    text: `${label}: ${record.lastFailure.failureClass} failure at ${at}, ${count} ${noun} in ${windowMinutes} min`,
    failing: true
  };
}

// =========================================
// Component
// =========================================

const Dashboard = () => {
  // Hero card data
  const [activity, setActivity] = useState({
    active: 0, idle: 0, dormant: 0, total: 0, waitlistPending: 0
  });

  // Stacked bar chart data
  const [chartData, setChartData] = useState([]);
  const [timeframe, setTimeframe] = useState('30d');

  // Platform totals
  const [totals, setTotals] = useState({
    users: 0, documents: 0, entries: 0, thoughtRate: 0
  });

  // User growth sparkline data
  const [growthData, setGrowthData] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // AI provider health line; null until loaded, 'unavailable' on error
  const [providerHealth, setProviderHealth] = useState(null);

  // ------------------------------------------
  // Data Fetching
  // ------------------------------------------

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [activityRes, knowledgeRes, userRes, docRes] = await Promise.all([
        apiService.admin.getActivitySummary(),
        apiService.admin.getKnowledgeMetrics(timeframe),
        apiService.admin.getUserMetrics(timeframe),
        apiService.admin.getDocumentMetrics(),
      ]);

      // Hero cards
      setActivity({
        active:          activityRes.active  ?? 0,
        idle:            activityRes.idle    ?? 0,
        dormant:         activityRes.dormant ?? 0,
        total:           activityRes.total   ?? 0,
        waitlistPending: activityRes.waitlistPending ?? 0,
      });

      // Stacked bar: split entries into focus-only and with-thought
      // Note: the /metrics/knowledge endpoint already maps _id → date
      const dailyData = (knowledgeRes.data || []).map(day => {
        const entries  = day.entries  || 0;
        const thoughts = day.thoughts || 0;
        return {
          date:         day.date,
          focusOnly:    Math.max(entries - thoughts, 0),
          withThought:  thoughts,
        };
      });
      setChartData(dailyData);

      // Platform totals
      const totalEntries  = dailyData.reduce((s, d) => s + d.focusOnly + d.withThought, 0);
      const totalThoughts = dailyData.reduce((s, d) => s + d.withThought, 0);
      setTotals({
        users:      userRes.totalUsers          || 0,
        documents:  docRes.totalDocuments        || 0,
        entries:    totalEntries,
        thoughtRate: totalEntries > 0
          ? Math.round((totalThoughts / totalEntries) * 100)
          : 0,
      });

      // User growth sparkline (cumulative registrations per day)
      const growthFormatted = (userRes.data || []).map(day => ({
        date:     day._id,
        newUsers: day.newUsers || 0,
      }));
      setGrowthData(growthFormatted);

    } catch (err) {
      console.error('Error fetching overview data:', err);
      setError('Failed to load overview data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [timeframe]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    let cancelled = false;
    apiService.admin.getProviderHealth()
      .then(data => { if (!cancelled) setProviderHealth(data); })
      .catch(() => { if (!cancelled) setProviderHealth('unavailable'); });
    return () => { cancelled = true; };
  }, []);

  // ------------------------------------------
  // Render Helpers
  // ------------------------------------------

  if (isLoading) {
    return <div className="loading-message">Loading overview…</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="dashboard">

      {/* ============ AI PROVIDER HEALTH ============ */}
      {providerHealth && (
        <div className="provider-health" aria-label="AI provider health">
          {providerHealth === 'unavailable'
            ? <span className="provider-health-item">Provider health unavailable</span>
            : Object.entries(providerHealth.providers || {}).map(([key, record]) => {
                const line = providerHealthText(key, record, providerHealth.windowMinutes || 15);
                return (
                  <span
                    key={key}
                    className={`provider-health-item${line.failing ? ' provider-health-item--failing' : ''}`}
                  >
                    {line.text}
                  </span>
                );
              })}
        </div>
      )}

      {/* ============ HERO CARDS ============ */}
      <div className="hero-cards">
        <div className="hero-card hero-card--active">
          <h3>Active Users</h3>
          <div className="hero-value">{activity.active}</div>
          <div className="hero-subtitle">of {activity.total} · ≤ 3 days</div>
        </div>

        <div className="hero-card hero-card--idle">
          <h3>Idle Users</h3>
          <div className="hero-value">{activity.idle}</div>
          <div className="hero-subtitle">4–16 days</div>
        </div>

        <div className="hero-card hero-card--dormant">
          <h3>Dormant Users</h3>
          <div className="hero-value">{activity.dormant}</div>
          <div className="hero-subtitle">17+ days</div>
        </div>

        <div className="hero-card hero-card--neutral">
          <h3>Waitlist Pending</h3>
          <div className="hero-value">{activity.waitlistPending}</div>
          <div className="hero-subtitle">awaiting review</div>
        </div>
      </div>

      {/* ============ STACKED BAR CHART ============ */}
      <div className="chart-container">
        <div className="chart-header">
          <h2 className="chart-title">Knowledge Entries</h2>
          <div className="time-filter">
            {TIMEFRAMES.map(tf => (
              <button
                key={tf.key}
                className={timeframe === tf.key ? 'active' : ''}
                onClick={() => setTimeframe(tf.key)}
              >
                {tf.label}
              </button>
            ))}
          </div>
        </div>

        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={chartData} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDateTick}
              tick={{ fontSize: 12, fill: '#888' }}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 12, fill: '#888' }}
            />
            <Tooltip content={<StackedBarTooltip />} />
            <Legend
              formatter={(value) =>
                value === 'focusOnly' ? 'Focus only' : 'With thought'
              }
            />
            <Bar
              dataKey="focusOnly"
              stackId="entries"
              name="focusOnly"
              fill={COLORS.blue}
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="withThought"
              stackId="entries"
              name="withThought"
              fill={COLORS.green}
              radius={[3, 3, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* ============ SUPPORTING PANELS ============ */}
      <div className="supporting-panels">
        {/* Left: Platform Totals */}
        <div className="supporting-panel">
          <h3 className="panel-title">Platform Totals</h3>
          <div className="totals-grid">
            <div className="total-item">
              <span className="total-label">Users</span>
              <span className="total-value">{totals.users}</span>
            </div>
            <div className="total-item">
              <span className="total-label">Documents</span>
              <span className="total-value">{totals.documents}</span>
            </div>
            <div className="total-item">
              <span className="total-label">Entries</span>
              <span className="total-value">{totals.entries}</span>
            </div>
            <div className="total-item">
              <span className="total-label">Thought Rate</span>
              <span className="total-value">{totals.thoughtRate}%</span>
            </div>
          </div>
        </div>

        {/* Right: User Growth */}
        <div className="supporting-panel">
          <h3 className="panel-title">User Growth</h3>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={growthData} barCategoryGap="15%">
              <XAxis
                dataKey="date"
                tickFormatter={formatDateTick}
                tick={{ fontSize: 10, fill: '#aaa' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis hide allowDecimals={false} />
              <Tooltip
                formatter={(value) => [value, 'New users']}
                labelFormatter={formatDateTick}
              />
              <Bar dataKey="newUsers" fill={COLORS.purple} radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
